import React, { useEffect, useState } from 'react';
import useStore from '../../../services/hooks/useStore';
import { useLocalStore, observer } from 'mobx-react-lite';
import { Modal, ModalHeader, ModalBody, Footer } from '../../../components/Modais/styles';
import { TextField, Checkbox, FormControlLabel, IconButton, Grid } from '@material-ui/core';
import Botao from '../../../components/Botoes/Botao';

import X9Logger from '../../../services/X9Logger';
import DeleteIcon from '@material-ui/icons/Delete';
import { apiV2 } from '../../../services/utils';
import NewSelect from '../../../components/Select/newSelect';

type Props = ModalProps & {
  categoriaNFeUuid: string;
  onSuccess: () => void;
};

type CFOPOption = {
  label: string;
  value: string;
  descricao?: string;
};

const FormCategoriaNFe: React.FC<Props> = ({ open, handleClose, categoriaNFeUuid, onSuccess }) => {
  const store = useStore();
  const [categoriasOptions, setCategoriasOptions] = useState<SelectRow[]>([]);
  const [cfopOptions, setCfopOptions] = useState<CFOPOption[]>([]);
  const [loading, setLoading] = useState(false);


  const categoriaNFe = categoriaNFeUuid ? store.categoriasNFe.getByUuid(categoriaNFeUuid) : null;

  const unidadeMedidaOptions = [
    { label: 'UN', value: 'un' },
    { label: 'KG', value: 'kg' },
  ];

  const state = useLocalStore(() => ({
    categoria: null as SelectRow | null,
    ncm: '',
    unidadeMedida: null as SelectRow | null,
    descricaoFiscal: '',
    incluirNomeEspecie: false,
    codigoProduto: '',
    icmsNota: false,
    pis: '',
    pisCst: '',
    cofins: '',
    cofinsCst: '',
    cfop: null as SelectRow | null,
    ativo: true,
    cfopSelecionado: null as CFOPOption | null,
    cfopsSelecionados: [] as Array<CFOPOption & { isPadrao: boolean }>,
    informacaoComplementar: '',
  }));

  useEffect(() => {
    if (open) {
      carregarDados();
      if (!categoriaNFe) {
        resetarFormulario();
      }
    }
  }, [open, categoriaNFeUuid]);

  async function carregarDados() {
    try {
      // Garantir que as categorias estejam carregadas
      if (!store.categorias.arr) {
        await store.categorias.populate();
      }

      // Carregar categorias que emitem NFe
      const categorias = store.categorias.arr?.filter((cat: any) => cat.emite_nfe) || [];
      setCategoriasOptions(
        categorias.map((cat: any) => ({
          label: cat.nome,
          value: cat.id.toString(),
        })),
      );
      const cfopResponse = await apiV2.get('/cfop-nfe/');
      const cfopData = cfopResponse.data.map((cfop: any) => ({
        label: `${cfop.n_cfop} - ${cfop.natureza_operacao}`,
        value: cfop.n_cfop.toString(),
        descricao: cfop.natureza_operacao,
      }));

    
      setCfopOptions(cfopData);
    } catch (error) {
      X9Logger.report(error as Error);
    }
  }

 

  useEffect(() => {
    if (open && categoriaNFe && categoriasOptions.length > 0 && cfopOptions.length > 0) {
      const categoria = categoriasOptions.find((opt) => opt.value === categoriaNFe.categoria_financeira.toString());
      state.categoria = categoria || null;
      state.ncm = categoriaNFe.ncm || '';
      state.unidadeMedida = unidadeMedidaOptions.find((opt) => opt.value === categoriaNFe.unidade_medida) || null;
      state.descricaoFiscal = categoriaNFe.descricao_fiscal || '';
      state.incluirNomeEspecie = Boolean(
        (categoriaNFe as any).inserir_especie ?? (categoriaNFe as any).incluir_nome_especie,
      );
      state.codigoProduto = categoriaNFe.codigo_produto || '';
      state.icmsNota = categoriaNFe.icms_nota || false;
      state.pis = categoriaNFe.pis || '';
      state.pisCst = categoriaNFe.pis_cst || '';
      state.cofins = categoriaNFe.cofins || '';
      state.cofinsCst = categoriaNFe.cofins_cst || '';
      state.ativo = categoriaNFe.ativo !== undefined ? categoriaNFe.ativo : true;
      (state as any).informacaoComplementar = (categoriaNFe as any).informacao_complementar || '';

      if (categoriaNFe.cfops && categoriaNFe.cfops.length > 0) {
        state.cfopsSelecionados = categoriaNFe.cfops.map((c) => {
          const cfopOption = cfopOptions.find((opt) => opt.value === c.cfop.toString());
          return {
            label: cfopOption?.label || `CFOP ${c.cfop}`, // Fallback label if not found in options
            value: c.cfop.toString(),
            descricao: cfopOption?.descricao,
            isPadrao: c.padrao,
          };
        });
      } else {
        // Fallback for legacy data without cfops array
        const cfop = cfopOptions.find((opt) => opt.value === categoriaNFe.cfop.toString());
        if (cfop) {
          state.cfopsSelecionados = [{ ...cfop, isPadrao: true }];
        }
      }
    }
  }, [open, categoriaNFeUuid, categoriasOptions, cfopOptions]);

  function resetarFormulario() {
    state.categoria = null;
    state.ncm = '';
    state.unidadeMedida = null;
    state.descricaoFiscal = '';
    state.incluirNomeEspecie = false;
    state.codigoProduto = '';
    state.icmsNota = false;
    state.pis = '';
    state.pisCst = '';
    state.cofins = '';
    state.cofinsCst = '';
    state.cfop = null;
    state.cfopSelecionado = null;
    state.cfopsSelecionados = [];
    state.ativo = true;
    state.informacaoComplementar = '';
  }

  function handleCFOPChange(cfopSelect: SelectRow | null) {
    state.cfop = cfopSelect;
    if (cfopSelect) {
      const cfopEncontrado = cfopOptions.find((opt) => opt.value === cfopSelect.value);
      state.cfopSelecionado = cfopEncontrado || null;
    } else {
      state.cfopSelecionado = null;
    }
  }

  function adicionarCFOP() {
    if (!state.cfopSelecionado) {
      store.notificar('Selecione um CFOP');
      return;
    }

    const cfopJaAdicionado = state.cfopsSelecionados.some(
      (cfop) => cfop.value === state.cfopSelecionado!.value
    );

    if (cfopJaAdicionado) {
      store.notificar('Este CFOP já foi adicionado');
      return;
    }

    const isPrimeiro = state.cfopsSelecionados.length === 0;
    state.cfopsSelecionados.push({
      ...state.cfopSelecionado,
      isPadrao: isPrimeiro,
    });

    state.cfop = null;
    state.cfopSelecionado = null;
  }

  function removerCFOP(index: number) {
    const cfopRemovido = state.cfopsSelecionados[index];
    state.cfopsSelecionados.splice(index, 1);

    if (cfopRemovido.isPadrao && state.cfopsSelecionados.length > 0) {
      state.cfopsSelecionados[0].isPadrao = true;
    }
  }

  function definirCFOPPadrao(index: number) {
    state.cfopsSelecionados.forEach((cfop, i) => {
      cfop.isPadrao = i === index;
    });
  }

  async function salvar(): Promise<void> {
    if (!state.categoria) {
      store.notificar('Selecione a categoria');
      return;
    }
    if (!state.ncm) {
      store.notificar('Informe o código NCM');
      return;
    }
    if (!state.unidadeMedida) {
      store.notificar('Selecione a unidade de medida');
      return;
    }
    if (!state.descricaoFiscal) {
      store.notificar('Informe a descrição do produto');
      return;
    }
    if (state.cfopsSelecionados.length === 0) {
      store.notificar('Adicione pelo menos um CFOP');
      return;
    }

    setLoading(true);
    try {
      const cfopPadrao = state.cfopsSelecionados.find((cfop) => cfop.isPadrao);

      const dados = {
        categoria_financeira: parseInt(state.categoria.value),
        ncm: state.ncm,
        cfop: parseInt(cfopPadrao!.value),
        cofins: state.icmsNota ? state.cofins : '',
        cofins_cst: state.icmsNota ? state.cofinsCst : '',
        pis: state.icmsNota ? state.pis : '',
        pis_cst: state.icmsNota ? state.pisCst : '',
        unidade_medida: state.unidadeMedida.value,
        descricao_fiscal: state.descricaoFiscal,
        inserir_especie: state.incluirNomeEspecie,
        codigo_produto: state.codigoProduto || '',
        icms_nota: state.icmsNota,
        cfops: state.cfopsSelecionados.map((c) => ({
          cfop_id: parseInt(c.value),
          padrao: c.isPadrao,
        })),
        ativo: state.ativo,
        informacao_complementar: state.informacaoComplementar || '',
      };

      if (categoriaNFeUuid) {
        await store.categoriasNFe.editar(categoriaNFeUuid, dados);
      } else {
        await store.categoriasNFe.criar(dados);
      }

      onSuccess();
      handleClose();
      resetarFormulario();
    } catch (error: any) {
      X9Logger.report(error as Error);
      if (error.response?.data) {
        const errors = error.response.data;
        if (typeof errors === 'object') {
          Object.values(errors).forEach((value) => {
            store.notificar(`${value}`);
          });
        } else {
          store.notificar('Erro ao salvar categoria NFe. Tente novamente.');
        }
      } else {
        store.notificar('Erro ao salvar categoria NFe. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal onClose={handleClose} open={open}>
      <ModalHeader>Categoria NFe</ModalHeader>
      <ModalBody style={{ width: '600px', maxHeight: '80vh', overflowY: 'auto' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} style={{ paddingTop: '60px' }}>
            <NewSelect
              value={state.categoria}
              placeholder="Selecione a categoria"
              onChange={(e: any) => (state.categoria = e)}
              options={categoriasOptions}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField

              value={state.ncm}
              onChange={(e) => (state.ncm = e.target.value)}
              label="Código NCM"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} style={{ paddingTop: '40px' }}>
            <NewSelect
              value={state.unidadeMedida}
              placeholder="Unidade Medida"
              onChange={(e: any) => (state.unidadeMedida = e)}
              options={unidadeMedidaOptions}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              value={state.descricaoFiscal}
              onChange={(e) => (state.descricaoFiscal = e.target.value)}
              label="Descrição do Produto"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.incluirNomeEspecie}
                  onChange={(e) => (state.incluirNomeEspecie = e.target.checked)}
                  color="primary"
                />
              }
              label="Exibir espécie na descrição do produto! (Ex. Venda de Peixe / Tilápia)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              value={state.informacaoComplementar}
              onChange={(e) => {
                const val = e.target.value.slice(0, 500);
                state.informacaoComplementar = val;
              }}
              label="Informação Complementar (até 500 caracteres)"
              fullWidth
              multiline
              rows={3}
              helperText={`${state.informacaoComplementar.length}/500 caracteres`}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              value={state.codigoProduto}
              onChange={(e) => (state.codigoProduto = e.target.value)}
              label="Código Produto"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.icmsNota}
                  onChange={(e) => (state.icmsNota = e.target.checked)}
                  color="primary"
                />
              }
              label="ICMS na NF"
            />
          </Grid>

          {state.icmsNota && (
            <>
              <Grid item xs={6}>
                <TextField
                  value={state.pis}
                  onChange={(e) => (state.pis = e.target.value)}
                  label="PIS (%)"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  value={state.pisCst}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                    state.pisCst = val;
                  }}
                  label="PIS CST"
                  fullWidth
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  value={state.cofins}
                  onChange={(e) => (state.cofins = e.target.value)}
                  label="COFINS (%)"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  value={state.cofinsCst}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                    state.cofinsCst = val;
                  }}
                  label="COFINS CST"
                  fullWidth
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>
            </>
          )}


          <Grid item xs={12} container spacing={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ width: '69%', marginBottom:'-25px' }}>
              <NewSelect
                value={state.cfop}
                placeholder="CFOP"
                onChange={(e: any) => handleCFOPChange(e)}
                options={cfopOptions}
              />
            </div>
            <div style={{ width: '29%' }}>
              <Botao
                onClick={adicionarCFOP}
                variant="contained"
                style={{
                  width: '100%',
                  backgroundColor: '#039BE5',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  marginLeft: 'auto'
                }}
              >
                Adicionar
              </Botao>
            </div>
          </Grid>

          {state.cfopsSelecionados.length > 0 && (
            <Grid item xs={12}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px', color: '#666', fontSize: '12px' }}>
                <div style={{ width: '60px' }}>CFOP</div>
                <div style={{ flex: 1 }}>Tipo de Operação</div>
                <div style={{ width: '60px', textAlign: 'center' }}>Padrão?</div>
                <div style={{ width: '30px' }}></div>
              </div>

              {state.cfopsSelecionados.map((cfop, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <div style={{ width: '60px', textDecoration: 'underline' }}>{cfop.label.split(' - ')[0]}</div>
                  <div style={{ flex: 1, textTransform: 'uppercase', fontSize: '13px' }}>
                    {cfop.descricao || cfop.label.split(' - ')[1]}
                  </div>
                  <div style={{ width: '60px', display: 'flex', justifyContent: 'center' }}>
                    <Checkbox
                      checked={cfop.isPadrao}
                      onChange={() => definirCFOPPadrao(index)}
                      color="primary"
                      size="small"
                      style={{ padding: 0 }}
                    />
                  </div>
                  <IconButton
                    onClick={() => removerCFOP(index)}
                    size="small"
                    style={{
                      color: '#fff',
                      backgroundColor: '#f44336',
                      borderRadius: '4px',
                      padding: '4px',
                      width: '24px',
                      height: '24px'
                    }}
                  >
                    <DeleteIcon style={{ fontSize: '16px' }} />
                  </IconButton>
                </div>
              ))}
            </Grid>
          )}
        </Grid>
      </ModalBody>
      <Footer>
        <Botao onClick={handleClose} variant="contained" cor="#f44336" disabled={loading}>
          Cancelar
        </Botao>
        <Botao onClick={salvar} variant="contained" cor="#00C853" disabled={loading}>
          {loading ? 'Salvando...' : categoriaNFeUuid ? 'Salvar' : 'Adicionar'}
        </Botao>
      </Footer>
    </Modal>
  );
};

export default observer(FormCategoriaNFe);

